import { useLogger } from '../composables/useLogger'
import { resolveSeoHeadLinkHref } from './seo.utils'
import type { SeoDraft, SeoHeadLink, SeoRobots } from '../types/seo'

/**
 * Turn SEO values into the exact strings and tag lists the document head expects.
 *
 * Pure functions only — `useSeo` decides *what* a page says, this module formats it.
 */

export function serializeSeoRobots(robots: SeoRobots): string {
  const parts = [robots.index ? 'index' : 'noindex', robots.follow ? 'follow' : 'nofollow']
  if (robots.noarchive) parts.push('noarchive')
  if (robots.nosnippet) parts.push('nosnippet')
  if (typeof robots.maxSnippet === 'number') parts.push(`max-snippet:${robots.maxSnippet}`)
  if (robots.maxImagePreview) parts.push(`max-image-preview:${robots.maxImagePreview}`)
  if (typeof robots.maxVideoPreview === 'number') parts.push(`max-video-preview:${robots.maxVideoPreview}`)
  return parts.join(', ')
}

/**
 * Serialize JSON-LD for an inline `<script>` tag.
 * Escapes `<` so user-controlled content cannot break out of the script context.
 */
export function serializeSeoJsonLd(jsonLd: SeoDraft['jsonLd']): string {
  return JSON.stringify(jsonLd).replace(/</g, '\\u003c')
}

/**
 * Resolve extra `<link>` tags against `baseUrl`.
 *
 * `rel="canonical"` is dropped: the canonical link is always rendered from the resolved
 * `canonicalUrl`, and a second one would give crawlers conflicting signals. Use
 * `draft.canonical` instead.
 */
export function resolveSeoHeadLinks(headLinks: SeoHeadLink[] | undefined, fallbackPath: string, baseUrl: string): SeoHeadLink[] {
  if (!headLinks?.length) return []

  const resolved: SeoHeadLink[] = []
  for (const link of headLinks) {
    if (link.rel.trim().toLowerCase() === 'canonical') {
      const { logger } = useLogger()
      logger.error(`[seo] Ignoring headLinks rel="canonical" (${link.href}). Set draft.canonical instead — only one canonical is emitted.`)
      continue
    }
    resolved.push({
      rel: link.rel,
      href: resolveSeoHeadLinkHref(link.href, fallbackPath, baseUrl),
    })
  }
  return resolved
}
